var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faCloudArrowDown, faSquarePen } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, FormControlLabel, IconButton, Link as MuiLink, Radio, RadioGroup, Stack, Typography, outlinedInputClasses, styled, } from '@mui/material';
import { GridFooter, gridClasses } from '@mui/x-data-grid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Worktime } from '../../../enum';
import { RumEntityLabel, rumEntityName } from '../../../observability/rumEntity';
import { useTerminologyTranslation } from '../../app/terminology';
import { Autocomplete, ColumnFilterHeader, ConfirmDialog, DataGrid, ErrorMessageBox, FallbackComponent, useClientTableFilter, } from '../../common';
import { NoRowsOverlay } from '../../common/dataGrid/slots';
import { SelectOrCustomField } from '../../common/selectOrCustomField';
import { useDialog } from '../../hooks';
import { useIsPlanLocked, usePlanAPI, usePlanTypeCategory } from '../selectors';
import { MultiskillWarningDialog } from './multiskillWarningDialog';
import { NEW_LOB_OPTION_ID, QUEUE_NAME_MAX_LENGTH, sortQueuesByLob, useBulkDeleteQueueAPI, useCancelAndDeleteMultiskill, useDeleteQueueAPI, useLanguageOptions, useLobsAPI, useQueueAPI, useQueuesAPI, useUpdateQueueAPI, useWorkTypeAPI, } from './selectors';
import { DialogMode, WorkType } from './types';
var PAGE_SIZE = 10;
var StyledStack = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        paddingTop: theme.spacing(2.3925),
        paddingBottom: theme.spacing(2.3925),
    });
});
var QueuesGrid = styled(DataGrid)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {},
        // Allow horizontal scrolling on smaller screens so all columns stay reachable
        // (the shared DataGrid forces overflowX: 'hidden' on the virtual scroller).
        _b["& .".concat(gridClasses.virtualScroller)] = {
            overflowX: 'auto',
        },
        _b["& .".concat(gridClasses.cell, ":has(.").concat(outlinedInputClasses.root, ")")] = {
            padding: 0,
        },
        _b["& .".concat(gridClasses.cell, " .MuiOutlinedInput-root:not(.Mui-focused) .").concat(outlinedInputClasses.notchedOutline)] = {
            border: 'none',
        },
        _b["& .".concat(gridClasses.columnHeaderCheckbox, " .").concat(gridClasses.columnHeaderTitleContainer, " .MuiCheckbox-root .MuiSvgIcon-root")] = {
            color: theme.palette.common.white,
        },
        _b);
});
var FooterRow = styled(Stack)({
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
});
var FooterLeft = styled(Box)({
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-start',
});
var FooterCenter = styled(Box)({
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
});
var FooterRight = styled(Box)({
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
});
var DEFERRABLE_DISABLED_WORKTYPES = new Set([WorkType.InboundCalls, WorkType.Chat]);
// Inline dropdown option ids are strings
var NEW_OPTION_ID = String(NEW_LOB_OPTION_ID);
var sameValue = function (a, b) {
    return typeof a === 'boolean' || typeof b === 'boolean' ? Boolean(a) === Boolean(b) : a === b;
};
var QueueGridFooter = function (_a) {
    var selectedCount = _a.selectedCount, onDelete = _a.onDelete;
    var t = useTerminologyTranslation(['wizard', 'translation']).t;
    var showFooter = selectedCount > 0;
    return (_jsxs(FooterRow, { children: [_jsx(FooterLeft, { children: showFooter && (_jsx(Typography, __assign({ paddingLeft: 4, variant: "body2", color: "text.secondary" }, { children: t('queue.form.bulkDeleteQueue.selectedCount', {
                        count: selectedCount,
                    }) }))) }), _jsx(FooterCenter, { children: _jsx(GridFooter, {}) }), _jsx(FooterRight, { children: showFooter && (_jsx(Button, __assign({ variant: "outlined", color: "error", onClick: onDelete, sx: { textTransform: 'uppercase' } }, { children: t('queue.form.bulkDeleteQueue.deleteSelected') }))) })] }));
};
/**
 * Org-config LOBs a queue may be assigned, scoped to the plan's data source. A manual plan gets the
 * client's BU-less LOBs. An imported plan (businessUnitId set) gets the LOBs under its chosen business
 * unit PLUS any BU-less ("shared") LOBs, which aren't tied to a business unit and so are available to
 * every plan. Per-plan sandbox LOBs are always assignable regardless of scope.
 */
export var scopeAssignableLobs = function (lobs, businessUnitId) {
    return lobs.filter(function (l) { return l.isSandbox || l.buId == null || l.buId === businessUnitId; });
};
export var buildUpdatePayload = function (queue, overrides) {
    var _a, _b, _c, _d, _e;
    return (__assign({ businessArea: queue.businessArea, lobId: (_a = queue.lobId) !== null && _a !== void 0 ? _a : null, subLobId: (_b = queue.subLobId) !== null && _b !== void 0 ? _b : null, worktypeId: queue.worktype.id, worktime: queue.worktime, languages: ((_c = queue.languages) === null || _c === void 0 ? void 0 : _c.length) ? queue.languages : undefined, sandboxLobName: queue.isSandboxLob ? (_d = queue.lobName) !== null && _d !== void 0 ? _d : '' : '', sandboxSubLobName: queue.isSandboxSubLob ? (_e = queue.subLobName) !== null && _e !== void 0 ? _e : '' : '' }, overrides));
};
export var QueuesTable = function (_a) {
    var _b, _c;
    var handleOpenAddQueueDialog = _a.handleOpenAddQueueDialog;
    var planTypeCategory = usePlanTypeCategory();
    var _d = useQueuesAPI(), queuesData = _d[0], fetchQueues = _d[1];
    var _e = useWorkTypeAPI(), workTypeData = _e[0], fetchWorkType = _e[1];
    var _f = useState(), queueDeleteId = _f[0], setQueueDeleteId = _f[1];
    var _g = useState(), queueUpdateId = _g[0], setQueueUpdateId = _g[1];
    var _h = useState(), updatePayload = _h[0], setUpdatePayload = _h[1];
    var fetchCancelAndDeleteMultiskill = useCancelAndDeleteMultiskill();
    var _j = useDeleteQueueAPI(queueDeleteId), deleteQueueData = _j[0], deleteQueue = _j[1], clearDeleteQueue = _j[2];
    var _k = useUpdateQueueAPI(queueUpdateId), updateQueueData = _k[0], updateQueue = _k[1], clearUpdateQueueData = _k[2];
    var _l = useState([]), rowSelectionModel = _l[0], setRowSelectionModel = _l[1];
    var _m = useState(false), bulkDeleteConfirmOpen = _m[0], setBulkDeleteConfirmOpen = _m[1];
    var _o = useBulkDeleteQueueAPI(), bulkDeleteQueueData = _o[0], bulkDeleteQueue = _o[1], clearBulkDeleteQueue = _o[2];
    var _p = useQueueAPI(), fetchQueueDetail = _p[1];
    var t = useTerminologyTranslation(['wizard', 'translation']).t;
    var languages = useLanguageOptions();
    var planData = usePlanAPI()[0];
    var _q = useLobsAPI(), lobData = _q[0], fetchLob = _q[1];
    useEffect(function () {
        fetchLob();
    }, [fetchLob]);
    // Queues can be assigned org-config LOBs scoped to the plan's data source: an imported plan's
    // queues get the LOBs under its chosen business unit plus any BU-less ("shared") LOBs; a manual
    // plan's queues get the client's BU-less LOBs. Per-plan sandbox LOBs are always assignable.
    var businessUnitId = (_c = (_b = planData.data) === null || _b === void 0 ? void 0 : _b.businessUnitId) !== null && _c !== void 0 ? _c : null;
    var assignableLobs = useMemo(function () { var _a; return scopeAssignableLobs((_a = lobData.data) !== null && _a !== void 0 ? _a : [], businessUnitId); }, [lobData.data, businessUnitId]);
    var lobOptions = useMemo(function () { return assignableLobs.map(function (l) { return ({ id: l.id, name: l.lobName }); }); }, [assignableLobs]);
    var languageOptions = useMemo(function () { return languages.map(function (l) { return ({ id: l.code, name: l.name }); }); }, [languages]);
    var subLobsByLobId = useMemo(function () { return new Map(assignableLobs.map(function (l) { var _a; return [l.id, (_a = l.subLobs) !== null && _a !== void 0 ? _a : []]; })); }, [assignableLobs]);
    var isPlanLocked = useIsPlanLocked();
    var newOptionLabel = t('queue.form.addQueue.new');
    var lobSelectOptions = useMemo(function () { return __spreadArray(__spreadArray([], assignableLobs.map(function (l) { return ({ id: String(l.id), name: l.lobName }); }), true), [
        { id: NEW_OPTION_ID, name: newOptionLabel },
    ], false); }, [assignableLobs, newOptionLabel]);
    var buildSubLobOptions = useCallback(function (lobId) {
        var _a;
        return __spreadArray(__spreadArray([], ((_a = subLobsByLobId.get(lobId !== null && lobId !== void 0 ? lobId : -1)) !== null && _a !== void 0 ? _a : []).map(function (s) { return ({ id: String(s.id), name: s.subLobName }); }), true), [
            { id: NEW_OPTION_ID, name: newOptionLabel },
        ], false);
    }, [subLobsByLobId, newOptionLabel]);
    var _r = useState({}), optimisticById = _r[0], setOptimisticById = _r[1];
    var setOptimistic = useCallback(function (id, patch) {
        setOptimisticById(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[id] = __assign(__assign({}, prev[id]), patch), _a)));
        });
    }, []);
    // Rows whose save came back OK and whose refetch is still on its way. The next queues payload is the
    // saved one for them, so the server row wins outright instead of having to match the overlay field by
    // field: a LOB / Sub-LOB created from a custom name is given its id by the server, which the overlay
    // can't predict and would otherwise pin to null for as long as the step stays open.
    var savedIdsRef = useRef(new Set());
    var dropOptimistic = useCallback(function (id) {
        setOptimisticById(function (prev) {
            if (!(id in prev))
                return prev;
            var rest = __assign({}, prev);
            delete rest[id];
            return rest;
        });
    }, []);
    useEffect(function () {
        var server = queuesData.data;
        if (!server)
            return;
        var saved = savedIdsRef.current;
        savedIdsRef.current = new Set();
        setOptimisticById(function (prev) {
            var ids = Object.keys(prev);
            if (!ids.length)
                return prev;
            var byId = new Map(server.map(function (q) { return [q.id, q]; }));
            var next = {};
            var changed = false;
            var _loop_1 = function (key) {
                var id = Number(key);
                var override = prev[id];
                var srv = byId.get(id);
                var reconciled = !srv ||
                    saved.has(id) ||
                    Object.entries(override).every(function (_a) {
                        var field = _a[0], value = _a[1];
                        return sameValue(srv[field], value);
                    });
                if (reconciled)
                    changed = true;
                else
                    next[id] = override;
            };
            for (var _i = 0, ids_1 = ids; _i < ids_1.length; _i++) {
                var key = ids_1[_i];
                _loop_1(key);
            }
            return changed ? next : prev;
        });
    }, [queuesData.data]);
    useEffect(function () {
        if (updateQueueData.error)
            setOptimisticById({});
    }, [updateQueueData.error]);
    var queueIdSignature = useMemo(function () {
        var _a;
        return ((_a = queuesData.data) !== null && _a !== void 0 ? _a : [])
            .map(function (q) { return q.id; })
            .sort(function (a, b) { return a - b; })
            .join(',');
    }, [queuesData.data]);
    var orderedIds = useMemo(function () { var _a; return sortQueuesByLob((_a = queuesData.data) !== null && _a !== void 0 ? _a : []).map(function (q) { return q.id; }); }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queueIdSignature]);
    var displayRows = useMemo(function () {
        var _a;
        var byId = new Map(((_a = queuesData.data) !== null && _a !== void 0 ? _a : []).map(function (q) {
            var override = optimisticById[q.id];
            return [q.id, override ? __assign(__assign({}, q), override) : q];
        }));
        return orderedIds.map(function (id) { return byId.get(id); }).filter(function (q) { return !!q; });
    }, [queuesData.data, optimisticById, orderedIds]);
    var languageMap = useMemo(function () { return Object.fromEntries(languages.map(function (_a) {
        var code = _a.code, name = _a.name;
        return [code, name];
    })); }, [languages]);
    var filterColumns = useMemo(function () { return [
        { field: 'businessArea', filter: 'text', sortable: true },
        { field: 'lobName', filter: 'text', sortable: true },
        { field: 'subLobName', filter: 'text', sortable: true },
        { field: 'worktypeName', filter: 'enum', sortable: true },
        {
            field: 'worktime',
            filter: 'enum',
            sortable: true,
            getLabel: function (value) {
                return value === String(Worktime.Deferrable)
                    ? t('queue.form.queueType.deferrable')
                    : t('queue.form.queueType.nonDeferrable');
            },
        },
        { field: 'languagesDisplay', filter: 'text', sortable: true },
    ]; }, [t]);
    var getQueueValue = useCallback(function (row, field) {
        var _a, _b, _c;
        if (field === 'worktypeName')
            return ((_a = row.worktype) === null || _a === void 0 ? void 0 : _a.name) || '';
        if (field === 'languagesDisplay') {
            return ((_c = (_b = row.languages) === null || _b === void 0 ? void 0 : _b.map(function (code) { return languageMap[code]; }).filter(Boolean).join(', ')) !== null && _c !== void 0 ? _c : '');
        }
        if (field === 'lobName')
            return row.lobName || '';
        if (field === 'subLobName')
            return row.subLobName || '';
        return undefined;
    }, [languageMap]);
    var _s = useClientTableFilter({
        rows: displayRows,
        columns: filterColumns,
        getValue: getQueueValue,
    }), processedRows = _s.processedRows, getFilterState = _s.getFilterState, getAllValues = _s.getAllValues, applyFilter = _s.applyFilter;
    var makeFilterHeader = useCallback(function (field, label) { return function () { return (_jsx(ColumnFilterHeader, { label: label, filterState: getFilterState(field), allValues: getAllValues(field), onApply: function (update) { return applyFilter(field, update); } })); }; }, [applyFilter, getAllValues, getFilterState]);
    var _t = useDialog(), dialogMode = _t.dialogMode, selectedQueue = _t.data, handleOpenDialog = _t.handleOpenDialog, handleCloseDialog = _t.handleCloseDialog;
    var _u = useState(false), showMultiskillWarning = _u[0], setShowMultiskillWarning = _u[1];
    var _v = useState(null), confirmAction = _v[0], setConfirmAction = _v[1];
    var handleMultiskillWarning = useCallback(function (action) {
        setConfirmAction(function () { return action; });
        setShowMultiskillWarning(true);
    }, []);
    useEffect(function () {
        fetchQueues();
        fetchWorkType();
    }, [fetchQueues, fetchWorkType]);
    var handleDeleteQueue = useCallback(function (queue) {
        handleOpenDialog(DialogMode.Delete, queue);
    }, [handleOpenDialog]);
    var handleContinue = useCallback(function () {
        setQueueDeleteId(selectedQueue === null || selectedQueue === void 0 ? void 0 : selectedQueue.id);
        handleCloseDialog();
    }, [handleCloseDialog, selectedQueue === null || selectedQueue === void 0 ? void 0 : selectedQueue.id]);
    var handleOpenDeletionWarningDialog = useCallback(function (queue) {
        if (queue === null || queue === void 0 ? void 0 : queue.hasMultiskill) {
            handleMultiskillWarning(function () { return handleContinue(); });
        }
        else {
            handleDeleteQueue(queue);
        }
    }, [handleContinue, handleDeleteQueue, handleMultiskillWarning]);
    useEffect(function () {
        if (deleteQueueData.data) {
            fetchQueues();
            setQueueDeleteId(undefined);
        }
    }, [fetchQueues, deleteQueueData.data]);
    useEffect(function () {
        if (queueDeleteId) {
            deleteQueue();
        }
    }, [deleteQueue, queueDeleteId]);
    var selectedQueueIds = useMemo(function () { return rowSelectionModel; }, [rowSelectionModel]);
    var selectedQueuesHaveMultiskill = useMemo(function () { var _a; return ((_a = queuesData.data) !== null && _a !== void 0 ? _a : []).some(function (queue) { return selectedQueueIds.includes(queue.id) && queue.hasMultiskill; }); }, [queuesData.data, selectedQueueIds]);
    var handleOpenBulkDeleteConfirm = useCallback(function () {
        setBulkDeleteConfirmOpen(true);
    }, []);
    var handleBulkDeleteClick = useCallback(function () {
        if (selectedQueuesHaveMultiskill) {
            handleMultiskillWarning(handleOpenBulkDeleteConfirm);
        }
        else {
            handleOpenBulkDeleteConfirm();
        }
    }, [handleMultiskillWarning, handleOpenBulkDeleteConfirm, selectedQueuesHaveMultiskill]);
    var handleBulkDeleteConfirm = useCallback(function () {
        setBulkDeleteConfirmOpen(false);
        bulkDeleteQueue({ ids: selectedQueueIds });
    }, [bulkDeleteQueue, selectedQueueIds]);
    useEffect(function () {
        if (bulkDeleteQueueData.data) {
            fetchQueues();
            setRowSelectionModel([]);
            clearBulkDeleteQueue();
        }
    }, [fetchQueues, bulkDeleteQueueData.data, clearBulkDeleteQueue]);
    // Rows the user has edited; required-field validation (e.g. language) only surfaces once a row is
    // touched, mirroring the Update dialog — untouched rows stay clean even if a field is missing.
    var _w = useState(new Set()), touchedRowIds = _w[0], setTouchedRowIds = _w[1];
    var handleUpdateQueue = useCallback(function (queue, overrides) {
        setTouchedRowIds(function (prev) { return (prev.has(queue.id) ? prev : new Set(prev).add(queue.id)); });
        // This edit supersedes any earlier confirmed save, so the payload still in flight for it must not
        // hand the row back to the server and drop what the user just picked.
        savedIdsRef.current.delete(queue.id);
        setQueueUpdateId(queue.id);
        setUpdatePayload(buildUpdatePayload(queue, overrides));
    }, []);
    var handleUpdateWithWarning = useCallback(function (queue, overrides) {
        var update = function () { return handleUpdateQueue(queue, overrides); };
        if (queue === null || queue === void 0 ? void 0 : queue.hasMultiskill) {
            handleMultiskillWarning(update);
        }
        else {
            update();
        }
    }, [handleMultiskillWarning, handleUpdateQueue]);
    var handleChangeWorktime = useCallback(function (queue, value) {
        handleUpdateWithWarning(queue, {
            worktime: Number(value),
        });
    }, [handleUpdateWithWarning]);
    var handleChangeWorktypeId = useCallback(function (queue, newWorktypeId) {
        var isDeferrableDisabled = planTypeCategory === 'ProdHours' || DEFERRABLE_DISABLED_WORKTYPES.has(newWorktypeId);
        handleUpdateWithWarning(queue, {
            worktypeId: newWorktypeId,
            worktime: isDeferrableDisabled ? Worktime.NonDeferrable : queue.worktime,
        });
    }, [handleUpdateWithWarning, planTypeCategory]);
    var handleChangeLob = useCallback(function (queue, newLobId) {
        var _a, _b;
        // Changing the LOB invalidates any sub-LOB; clear sandbox names since this is an org-config assignment
        setOptimistic(queue.id, {
            lobId: newLobId,
            lobName: (_b = (_a = lobOptions.find(function (o) { return o.id === newLobId; })) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : null,
            subLobId: null,
            subLobName: null,
            isSandboxLob: false,
            isSandboxSubLob: false,
        });
        handleUpdateQueue(queue, {
            lobId: newLobId,
            subLobId: null,
            sandboxLobName: '',
            sandboxSubLobName: '',
        });
    }, [handleUpdateQueue, lobOptions, setOptimistic]);
    var handleChangeSubLob = useCallback(function (queue, newSubLobId) {
        var _a, _b, _c;
        var subLob = ((_b = subLobsByLobId.get((_a = queue.lobId) !== null && _a !== void 0 ? _a : -1)) !== null && _b !== void 0 ? _b : []).find(function (s) { return s.id === newSubLobId; });
        setOptimistic(queue.id, {
            subLobId: newSubLobId,
            subLobName: (_c = subLob === null || subLob === void 0 ? void 0 : subLob.subLobName) !== null && _c !== void 0 ? _c : null,
            isSandboxSubLob: false,
        });
        handleUpdateQueue(queue, {
            subLobId: newSubLobId,
            sandboxSubLobName: '',
        });
    }, [handleUpdateQueue, setOptimistic, subLobsByLobId]);
    var handleClearLob = useCallback(function (queue) {
        setOptimistic(queue.id, {
            lobId: null,
            lobName: null,
            subLobId: null,
            subLobName: null,
            isSandboxLob: false,
            isSandboxSubLob: false,
        });
        handleUpdateQueue(queue, { lobId: null, subLobId: null, sandboxLobName: '', sandboxSubLobName: '' });
    }, [handleUpdateQueue, setOptimistic]);
    var handleClearSubLob = useCallback(function (queue) {
        setOptimistic(queue.id, { subLobId: null, subLobName: null, isSandboxSubLob: false });
        handleUpdateQueue(queue, { subLobId: null, sandboxSubLobName: '' });
    }, [handleUpdateQueue, setOptimistic]);
    var handleSelectLob = useCallback(function (queue, value) {
        if (value == null) {
            handleClearLob(queue);
            return;
        }
        if (value === NEW_OPTION_ID) {
            setOptimistic(queue.id, {
                isSandboxLob: true,
                lobId: null,
                lobName: '',
                subLobId: null,
                subLobName: null,
                isSandboxSubLob: false,
            });
            return;
        }
        handleChangeLob(queue, Number(value));
    }, [handleChangeLob, handleClearLob, setOptimistic]);
    var handleSelectSubLob = useCallback(function (queue, value) {
        if (value == null) {
            handleClearSubLob(queue);
            return;
        }
        if (value === NEW_OPTION_ID) {
            setOptimistic(queue.id, { isSandboxSubLob: true, subLobId: null, subLobName: '' });
            return;
        }
        handleChangeSubLob(queue, Number(value));
    }, [handleChangeSubLob, handleClearSubLob, setOptimistic]);
    var serverQueueById = useMemo(function () { var _a; return new Map(((_a = queuesData.data) !== null && _a !== void 0 ? _a : []).map(function (q) { return [q.id, q]; })); }, [queuesData.data]);
    var handleBlurSandboxLob = useCallback(function (queue, value) {
        var _a;
        var name = value.trim();
        if (!name) {
            if ((_a = serverQueueById.get(queue.id)) === null || _a === void 0 ? void 0 : _a.isSandboxLob)
                handleClearLob(queue);
            else
                dropOptimistic(queue.id);
            return;
        }
        setOptimistic(queue.id, { isSandboxLob: true, lobId: null, lobName: name });
        handleUpdateQueue(queue, { lobId: null, subLobId: null, sandboxLobName: name, sandboxSubLobName: '' });
    }, [dropOptimistic, handleClearLob, handleUpdateQueue, serverQueueById, setOptimistic]);
    var handleBlurSandboxSubLob = useCallback(function (queue, value) {
        var _a;
        var name = value.trim();
        if (!name) {
            if ((_a = serverQueueById.get(queue.id)) === null || _a === void 0 ? void 0 : _a.isSandboxSubLob)
                handleClearSubLob(queue);
            else
                dropOptimistic(queue.id);
            return;
        }
        setOptimistic(queue.id, { isSandboxSubLob: true, subLobId: null, subLobName: name });
        handleUpdateQueue(queue, { subLobId: null, sandboxSubLobName: name });
    }, [dropOptimistic, handleClearSubLob, handleUpdateQueue, serverQueueById, setOptimistic]);
    var handleChangeLanguage = useCallback(function (queue, code) {
        handleUpdateQueue(queue, { languages: code ? [code] : undefined });
    }, [handleUpdateQueue]);
    useEffect(function () {
        if (updateQueueData.data) {
            if (queueUpdateId != null)
                savedIdsRef.current.add(queueUpdateId);
            fetchQueues();
            fetchLob();
            setQueueUpdateId(undefined);
            setUpdatePayload(undefined);
            clearUpdateQueueData();
        }
    }, [fetchQueues, fetchLob, updateQueueData.data, clearUpdateQueueData, queueUpdateId]);
    useEffect(function () {
        return function () {
            clearUpdateQueueData();
            fetchQueueDetail();
            clearDeleteQueue();
            clearBulkDeleteQueue();
        };
    }, [clearBulkDeleteQueue, clearDeleteQueue, clearUpdateQueueData, fetchQueueDetail]);
    useEffect(function () {
        if (queueUpdateId && updatePayload) {
            updateQueue(updatePayload);
        }
    }, [updateQueue, queueUpdateId, updatePayload]);
    var columns = useMemo(function () {
        var cols = [
            {
                field: 'businessArea',
                headerName: t('queue.form.addQueue.queueName'),
                flex: 2.2,
                minWidth: 220,
                sortable: false,
                renderHeader: makeFilterHeader('businessArea', t('queue.form.addQueue.queueName')),
                renderCell: function (params) { return (_jsxs(StyledStack, { children: [_jsx(MuiLink, __assign({ "data-testid": "button-open", component: "button", onClick: function () { return handleOpenAddQueueDialog(DialogMode.Update, params.row); }, title: params.row.businessArea, disabled: isPlanLocked, "data-dd-action-name": "Edit queue: ".concat(rumEntityName('queue', params.row.id)) }, { children: _jsx(RumEntityLabel, __assign({ kind: "queue", id: params.row.id, hasActionName: false }, { children: params.row.businessArea })) })), _jsx(Stack, __assign({ width: 24, sx: { color: 'grey.300' } }, { children: _jsx(FontAwesomeIcon, { icon: params.row.isImported ? faCloudArrowDown : faSquarePen }) }))] })); },
            },
            {
                field: 'lobName',
                headerName: t('queue.form.addQueue.lineOfBusiness'),
                flex: 1.4,
                minWidth: 180,
                sortable: false,
                renderHeader: makeFilterHeader('lobName', t('queue.form.addQueue.lineOfBusiness')),
                valueGetter: function (params) { return params.row.lobName || ''; },
                renderCell: function (params) {
                    var _a;
                    var isCustom = params.row.lobId == null && !!params.row.isSandboxLob;
                    return (_jsx(SelectOrCustomField, { isCustom: isCustom, name: "lobId-".concat(params.row.id), value: params.row.lobId == null ? '' : String(params.row.lobId), options: lobSelectOptions, defaultValue: "", placeholder: t('queue.form.addQueue.selectLineOfBusiness'), customName: "sandboxLobName-".concat(params.row.id), customValue: isCustom ? (_a = params.row.lobName) !== null && _a !== void 0 ? _a : '' : '', customPlaceholder: t('queue.form.addQueue.newLineOfBusiness'), onChange: function (value) { return handleSelectLob(params.row, value); }, onSelectChange: function () { return handleClearLob(params.row); }, onCustomChange: function (value) { return setOptimistic(params.row.id, { lobName: value }); }, onCustomBlur: function (value) { return handleBlurSandboxLob(params.row, value); }, disabled: isPlanLocked, maxLength: QUEUE_NAME_MAX_LENGTH }));
                },
            },
            {
                field: 'subLobName',
                headerName: t('queue.form.addQueue.subLineOfBusiness'),
                flex: 1.4,
                minWidth: 180,
                sortable: false,
                renderHeader: makeFilterHeader('subLobName', t('queue.form.addQueue.subLineOfBusiness')),
                valueGetter: function (params) { return params.row.subLobName || ''; },
                renderCell: function (params) {
                    var _a, _b;
                    var isCustom = params.row.subLobId == null && !!params.row.isSandboxSubLob;
                    var hasLob = params.row.lobId != null || !!params.row.isSandboxLob;
                    var isDisabled = !hasLob || isPlanLocked;
                    return (_jsx(SelectOrCustomField, { isCustom: isCustom, name: "subLobId-".concat(params.row.id), value: params.row.subLobId == null ? '' : String(params.row.subLobId), options: buildSubLobOptions((_a = params.row.lobId) !== null && _a !== void 0 ? _a : null), defaultValue: "", placeholder: isDisabled ? t('translation:autocomplete.none') : t('queue.form.addQueue.selectSubLineOfBusiness'), customName: "sandboxSubLobName-".concat(params.row.id), customValue: isCustom ? (_b = params.row.subLobName) !== null && _b !== void 0 ? _b : '' : '', customPlaceholder: t('queue.form.addQueue.newSubLineOfBusiness'), onChange: function (value) { return handleSelectSubLob(params.row, value); }, onSelectChange: function () { return handleClearSubLob(params.row); }, onCustomChange: function (value) { return setOptimistic(params.row.id, { subLobName: value }); }, onCustomBlur: function (value) { return handleBlurSandboxSubLob(params.row, value); }, disabled: isDisabled, maxLength: QUEUE_NAME_MAX_LENGTH, disableClearable: false }));
                },
            },
            {
                field: 'worktypeName',
                headerName: t('queue.form.workType'),
                flex: 1.3,
                minWidth: 150,
                sortable: false,
                renderHeader: makeFilterHeader('worktypeName', t('queue.form.workType')),
                valueGetter: function (params) { var _a; return ((_a = params.row.worktype) === null || _a === void 0 ? void 0 : _a.name) || ''; },
                renderCell: function (params) {
                    var _a, _b;
                    return (_jsx(Autocomplete, { options: (_a = workTypeData.data) !== null && _a !== void 0 ? _a : [], value: (_b = params.row.worktype) === null || _b === void 0 ? void 0 : _b.id, onChange: function (value) { return handleChangeWorktypeId(params.row, Number(value)); }, placeholder: t('queue.form.addQueue.selectWorkType'), size: "small", disabled: isPlanLocked }));
                },
            },
            {
                field: 'worktime',
                headerName: t('queue.form.workTime'),
                flex: 2,
                minWidth: 290,
                sortable: false,
                renderHeader: makeFilterHeader('worktime', t('queue.form.workTime')),
                valueGetter: function (params) { return params.row.worktime; },
                renderCell: function (params) { return (_jsxs(RadioGroup, __assign({ row: true, value: params.row.worktime, onChange: function (e) { return handleChangeWorktime(params.row, e.target.value); } }, { children: [_jsx(FormControlLabel, { value: Worktime.NonDeferrable, control: _jsx(Radio, { size: "small" }), label: t('queue.form.queueType.nonDeferrable'), disabled: isPlanLocked }), _jsx(FormControlLabel, { value: Worktime.Deferrable, control: _jsx(Radio, { size: "small" }), label: t('queue.form.queueType.deferrable'), disabled: DEFERRABLE_DISABLED_WORKTYPES.has(params.row.worktype.id) || isPlanLocked })] }))); },
            },
            {
                field: 'languagesDisplay',
                headerName: t('queue.form.addQueue.languages'),
                flex: 1,
                minWidth: 140,
                sortable: false,
                renderHeader: makeFilterHeader('languagesDisplay', t('queue.form.addQueue.languages')),
                valueGetter: function (_a) {
                    var _b, _c;
                    var row = _a.row;
                    return (_c = (_b = row.languages) === null || _b === void 0 ? void 0 : _b.map(function (code) { return languageMap[code]; }).filter(Boolean).join(', ')) !== null && _c !== void 0 ? _c : '';
                },
                renderCell: function (params) {
                    var _a, _b, _c;
                    return (_jsx(Autocomplete, { options: languageOptions, value: (_b = (_a = params.row.languages) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : undefined, onChange: function (value) { return handleChangeLanguage(params.row, value); }, placeholder: t('queue.form.addQueue.enterLanguage'), size: "small", 
                        // Language is required; flag inline (red border) only once the user has edited this row
                        error: touchedRowIds.has(params.row.id) && !((_c = params.row.languages) === null || _c === void 0 ? void 0 : _c.length), disabled: isPlanLocked }));
                },
            },
            {
                field: 'actions',
                headerName: '',
                sortable: false,
                width: 48,
                filterable: false,
                renderCell: function (params) { return (_jsx(IconButton, __assign({ size: "small", onClick: function () { return handleOpenDeletionWarningDialog(params.row); }, sx: {
                        color: function (theme) { return theme.palette.text.primary; },
                        '&:hover': {
                            color: function (theme) { return theme.palette.text.primary; },
                            backgroundColor: function (theme) { return theme.palette.action.hover; },
                        },
                    }, disabled: isPlanLocked }, { children: _jsx(FontAwesomeIcon, { icon: "trash-can" }) }))); },
            },
        ];
        if (planTypeCategory === 'ProdHours') {
            return cols.filter(function (col) { return col.field !== 'worktime'; });
        }
        return cols;
    }, [
        buildSubLobOptions,
        handleBlurSandboxLob,
        handleBlurSandboxSubLob,
        handleChangeLanguage,
        handleChangeWorktime,
        handleChangeWorktypeId,
        handleClearLob,
        handleClearSubLob,
        handleOpenAddQueueDialog,
        handleOpenDeletionWarningDialog,
        handleSelectLob,
        handleSelectSubLob,
        isPlanLocked,
        languageMap,
        languageOptions,
        lobSelectOptions,
        makeFilterHeader,
        planTypeCategory,
        setOptimistic,
        t,
        touchedRowIds,
        workTypeData.data,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(ErrorMessageBox, { errors: [deleteQueueData.error, updateQueueData.error, bulkDeleteQueueData.error] }), _jsx(FallbackComponent, __assign({ overlay: true, requesting: !!queuesData.requesting ||
                    !!deleteQueueData.requesting ||
                    !!updateQueueData.requesting ||
                    !!bulkDeleteQueueData.requesting }, { children: _jsxs(Box, { children: [_jsx(QueuesGrid, { pageSize: PAGE_SIZE, columns: columns, rows: processedRows, autoHeight: true, getRowHeight: function () { return 'auto'; }, localeText: {
                                noRowsLabel: t('noDataIndication', {
                                    ns: 'translation',
                                    defaultValue: 'There is no data to display',
                                }),
                            }, checkboxSelection: true, isRowSelectable: function () { return !isPlanLocked; }, rowSelectionModel: rowSelectionModel, onRowSelectionModelChange: setRowSelectionModel, hideFooterSelectedRowCount: true, slots: {
                                footer: function () { return (_jsx(QueueGridFooter, { selectedCount: selectedQueueIds.length, onDelete: handleBulkDeleteClick })); },
                                noRowsOverlay: NoRowsOverlay,
                                noResultsOverlay: NoRowsOverlay,
                            } }), _jsx(ConfirmDialog, { open: dialogMode === DialogMode.Delete, onClose: handleCloseDialog, title: t('queue.form.deleteQueue.title'), message: t('queue.form.deleteQueue.message'), confirmButtonText: t('queue.form.deleteQueue.confirmButtonText', { defaultValue: 'continue' }), onConfirm: handleContinue }), _jsx(ConfirmDialog, { open: bulkDeleteConfirmOpen, onClose: function () { return setBulkDeleteConfirmOpen(false); }, title: t('queue.form.bulkDeleteQueue.title', { count: selectedQueueIds.length }), message: t('queue.form.bulkDeleteQueue.message'), confirmButtonText: t('queue.form.bulkDeleteQueue.confirmButtonText', { defaultValue: 'continue' }), onConfirm: handleBulkDeleteConfirm }), _jsx(MultiskillWarningDialog, { open: showMultiskillWarning, onClose: function () {
                                setShowMultiskillWarning(false);
                                setConfirmAction(null);
                            }, onConfirm: function () {
                                if (confirmAction) {
                                    fetchCancelAndDeleteMultiskill();
                                    confirmAction();
                                }
                                setShowMultiskillWarning(false);
                                setConfirmAction(null);
                            } })] }) }))] }));
};
